import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { useNotification } from '../../../context/NotificationContext';
import { clinicAPI, doctorAPI, consultationAPI } from '../../../utils/api';

const ClinicDashboard = () => {
  // const { user } = useAuth();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const [stats, setStats] = useState({
    totalPatients: 0,
    todayAppointments: 0,
    pendingConsultations: 0,
    revenue: 0
  });
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [scheduleData, setScheduleData] = useState({ patientName: '', phone: '', providerId: '', date: '', time: '' });
  const [doctors, setDoctors] = useState([]);

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, appointmentsRes, doctorsRes] = await Promise.all([
        clinicAPI.getStats(),
        clinicAPI.getAppointments(),
        doctorAPI.getClinicDoctors() // assuming this endpoint exists
      ]);

      if (statsRes.data?.success) {
        setStats(statsRes.data.data || stats);
      }

      if (appointmentsRes.data?.success) {
        setAppointments(appointmentsRes.data.data || []);
      }

      if (doctorsRes?.data?.success) {
        setDoctors(doctorsRes.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const acceptConsultation = async (consultationId) => {
    try {
      const res = await doctorAPI.acceptConsultation(consultationId);
      if (res.data?.success) {
        showSuccess('Consultation accepted successfully');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to accept consultation');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to accept consultation');
    }
  };

  const rejectConsultation = async (consultationId) => {
    if (!window.confirm('Are you sure you want to decline this session?')) return;
    try {
      const res = await consultationAPI.reject(consultationId, { reason: 'rejected' });
      if (res.data?.success) {
        showSuccess('Session declined');
        fetchDashboardData();
      } else {
        showError(res.data?.message || 'Failed to decline session');
      }
    } catch (error) {
      showError(error.response?.data?.message || 'Failed to decline session');
    }
  };

  const handleQuickSchedule = async (e) => {
    e.preventDefault();
    if (!scheduleData.patientName || !scheduleData.phone || !scheduleData.doctorId || !scheduleData.date || !scheduleData.time) {
      return showError('Please fill all fields');
    }
    try {
      showSuccess('Quick Schedule requires backend patient-creation. Feature mocked for demo.');
      setShowScheduleModal(false);
      setScheduleData({ patientName: '', phone: '', doctorId: '', date: '', time: '' });
    } catch (error) {
      showError('Failed to schedule appointment');
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-yellow-500">Center Dashboard</h1>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Total Users</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.totalPatients}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Today's Sessions</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.todayAppointments}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Pending Sessions</h3>
          <p className="text-2xl font-bold text-yellow-500">{stats.pendingConsultations}</p>
        </div>
        <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
          <h3 className="text-gray-400 text-sm mb-2">Revenue</h3>
          <p className="text-2xl font-bold text-yellow-500">₹{stats.revenue}</p>
        </div>
      </div>

      {/* Pending Consultations */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Pending Sessions</h2>
        <div className="space-y-4">
          {appointments.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <p>No pending sessions</p>
            </div>
          ) : (
            appointments.map((appointment) => (
              <div key={appointment._id} className="flex justify-between items-center p-4 bg-white/5 border border-white/10 backdrop-blur-lg rounded-xl">
                <div>
                  <h3 className="font-medium">User: {appointment.patient_id?.name || 'Unknown'}</h3>
                  <p className="text-sm text-gray-400">Type: {appointment.consultation_type}</p>
                  <p className="text-sm text-gray-400">Provider: {appointment.doctor_id?.name}</p>
                  <p className="text-sm text-gray-400">Time: {appointment.createdAt ? new Date(appointment.createdAt).toLocaleString() : 'N/A'}</p>
                </div>
                <div className="flex space-x-2">
                  {appointment.status === 'in_progress' || appointment.status === 'scheduled' || (appointment.consultation_type === 'chat' && appointment.status === 'requested') ? (
                    <button
                      onClick={() => {
                        if (appointment.consultation_type === 'chat') {
                          navigate(`/chat/${appointment._id}`);
                        } else {
                          navigate(`/consultation/${appointment._id}`);
                        }
                      }}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium"
                    >
                      {appointment.consultation_type === 'chat' ? 'Open Chat' : 'Join Call'}
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => acceptConsultation(appointment._id)}
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium"
                      >
                        Accept
                      </button>
                      <button onClick={() => rejectConsultation(appointment._id)} className="bg-yellow-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-medium">
                        Decline
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl p-6">
        <h2 className="text-xl font-semibold mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <button onClick={() => setShowScheduleModal(true)} className="bg-yellow-500 text-dark py-3 px-4 rounded-lg font-medium hover:opacity-90">
            Schedule Session
          </button>
          <button onClick={() => navigate('/dashboard/clinic/doctors')} className="bg-gray-700 text-light py-3 px-4 rounded-lg font-medium hover:bg-gray-600">
            Manage Providers
          </button>
        </div>
      </div>

      {/* Quick Schedule Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white/5 border border-white/10 backdrop-blur-xl rounded-2xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-yellow-500 mb-4">Quick Schedule Appointment</h3>
            <form onSubmit={handleQuickSchedule} className="space-y-4">
              <div>
                <label className="block text-gray-400 text-sm mb-1">Patient Name</label>
                <input
                  type="text"
                  required
                  value={scheduleData.patientName}
                  onChange={e => setScheduleData({ ...scheduleData, patientName: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light text-sm outline-none focus:border-yellow-500"
                  placeholder="John Doe"
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Patient Phone</label>
                <input
                  type="tel"
                  required
                  value={scheduleData.phone}
                  onChange={e => setScheduleData({ ...scheduleData, phone: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light text-sm outline-none focus:border-yellow-500"
                  placeholder="+91..."
                />
              </div>
              <div>
                <label className="block text-gray-400 text-sm mb-1">Select Doctor</label>
                <select
                  required
                  value={scheduleData.doctorId}
                  onChange={e => setScheduleData({ ...scheduleData, doctorId: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light text-sm outline-none focus:border-yellow-500"
                >
                  <option value="">Choose a Doctor</option>
                  {doctors.map(d => (
                    <option key={d._id} value={d._id}>{d.name} ({d.specialization})</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Date</label>
                  <input
                    type="date"
                    required
                    value={scheduleData.date}
                    onChange={e => setScheduleData({ ...scheduleData, date: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light text-sm outline-none focus:border-yellow-500"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-sm mb-1">Time</label>
                  <input
                    type="time"
                    required
                    value={scheduleData.time}
                    onChange={e => setScheduleData({ ...scheduleData, time: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded p-2 text-light text-sm outline-none focus:border-yellow-500"
                  />
                </div>
              </div>
              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowScheduleModal(false)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 text-light py-2 rounded-lg font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-yellow-500 hover:bg-yellow-600 text-dark py-2 rounded-lg font-medium transition-colors"
                >
                  Schedule
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClinicDashboard;
